// Custom project breakpoints - using Bootstrap since variables don't include custom responsive breakpoints
const RESPONSIVE_BREAKPOINTS = {
    xs: 0,
    sm: 576,
    md: 768,
    lg: 992,
    xl: 1200,
    xxl: 1400,
    // custom mobile nav breakpoint
    'mobile-nav': 1100
};

// Use Bootstrap's md breakpoint to match SCSS media queries
const RESPONSIVE_BREAKPOINT = RESPONSIVE_BREAKPOINTS.md;

// Scroll amount based on grid system (could be derived from $grid-gutter-width: 48px * 4)
const SCROLL_AMOUNT = 192; // ~48px * 4 - aligns with grid system

export default function initTables() {
    let tables = document.querySelectorAll('.table-wrapper table');

    tables.forEach((table) => {
        // Skip tables with 'table-default' class
        if (table.classList.contains('table-default')) {
            return;
        }

        // Convert the first row to a thead with th elements
        setupTableHeader(table);
        
        // Determine table mode and initialize
        const mode = determineTableMode(table);
        initializeResponsiveTable(table, mode);
    });
}

function setupTableHeader(table) {
    let firstRow = table.querySelector('tbody > tr:first-child');
    
    if (firstRow) {
        // Create a new thead element
        let thead = document.createElement('thead');
        table.insertBefore(thead, table.firstChild);
        
        // Move the first row to the thead
        thead.appendChild(firstRow);
        
        // Convert td elements in the first row to th elements
        let firstRowCells = firstRow.querySelectorAll('td');
        firstRowCells.forEach((cell) => {
            let th = document.createElement('th');
            while (cell.firstChild) {
                th.appendChild(cell.firstChild);
            }
            // Copy attributes
            Array.from(cell.attributes).forEach(attr => {
                th.setAttribute(attr.name, attr.value);
            });
            cell.parentNode.replaceChild(th, cell);
        });
    }
}

function determineTableMode(table) {
    if (table.classList.contains('table-swipe')) {
        return 'swipe';
    }
    return 'stack'; // default mode
}

function initializeResponsiveTable(table, mode) {
    // Remove existing tablesaw classes first
    table.classList.remove('table-swipe');
    
    // Add our custom classes
    table.classList.add('tablesaw', `tablesaw-${mode}`);
    table.setAttribute('data-tablesaw-mode', mode);
    
    // Create and setup the table bar
    setupTableBar(table, mode);
    
    // Setup responsive behavior
    if (mode === 'stack') {
        setupStackMode(table);
    } else if (mode === 'swipe') {
        setupSwipeMode(table);
    }
    
    // Store breakpoint value on table for consistency with design system
    table.dataset.breakpoint = RESPONSIVE_BREAKPOINT;
    table.dataset.customSpacing = 'base-spacing'; // Reference to spacing system
}

function setupTableBar(table, mode) {
    const tableWrapper = table.closest('.table-wrapper');
    if (!tableWrapper) return;
    
    // Check if bar already exists
    let existingBar = tableWrapper.querySelector('.tablesaw-bar');
    if (existingBar) {
        existingBar.remove();
    }
    
    // Create table bar
    const bar = document.createElement('div');
    bar.className = `tablesaw-bar tablesaw-mode-${mode}`;
    
    // Insert bar before table
    tableWrapper.insertBefore(bar, table);
    
    // Add navigation controls for swipe mode
    if (mode === 'swipe') {
        setupSwipeControls(bar, table);
    }
}

function setupStackMode(table) {
    const headers = Array.from(table.querySelectorAll('thead th'));
    const rows = Array.from(table.querySelectorAll('tbody tr'));
    
    rows.forEach(row => {
        const cells = Array.from(row.querySelectorAll('td'));
        
        cells.forEach((cell, index) => {
            if (headers[index]) {
                // Clear existing labels
                const existingLabel = cell.querySelector('.tablesaw-cell-label');
                if (existingLabel) {
                    existingLabel.remove();
                }
                
                // Create label element with classes that reference typography system
                const label = document.createElement('b');
                label.className = 'tablesaw-cell-label';
                label.textContent = headers[index].textContent.trim();
                
                // Add data attribute to reference custom font system
                label.setAttribute('data-font-size', 'xxs-type');
                label.setAttribute('data-font-weight', 'weight-bold');
                
                // Create content wrapper
                const content = document.createElement('span');
                content.className = 'tablesaw-cell-content';
                
                // Move existing content to wrapper
                while (cell.firstChild) {
                    content.appendChild(cell.firstChild);
                }
                
                // Add label and content back to cell
                cell.appendChild(label);
                cell.appendChild(content);
            }
        });
    });
}

function setupSwipeMode(table) {
    // Add swipe-specific classes and behavior
    const firstColumn = table.querySelectorAll('th:first-child, td:first-child');
    firstColumn.forEach(cell => {
        cell.classList.add('tablesaw-swipe-cellpersist');
    });
    
    // Setup horizontal scrolling container
    const tableWrapper = table.closest('.table-wrapper');
    if (tableWrapper && !tableWrapper.classList.contains('tablesaw-overflow')) {
        tableWrapper.classList.add('tablesaw-overflow');
    }
}

function setupSwipeControls(bar, table) {
    const controlsDiv = document.createElement('div');
    controlsDiv.className = 'tablesaw-advance';
    
    const leftBtn = createNavButton('left', '‹', () => scrollTable(table, -SCROLL_AMOUNT));
    const rightBtn = createNavButton('right', '›', () => scrollTable(table, SCROLL_AMOUNT));
    
    controlsDiv.appendChild(leftBtn);
    controlsDiv.appendChild(rightBtn);
    bar.appendChild(controlsDiv);
    
    // Update button states on scroll
    const updateButtons = () => updateNavButtons(table, leftBtn, rightBtn);
    updateButtons();
    
    // Listen for scroll events to update button states
    const scrollContainer = table.closest('.tablesaw-overflow') || table.parentElement;
    scrollContainer.addEventListener('scroll', updateButtons);
    
    // Optional: Update buttons on window resize for responsive behavior
    // Use a simple throttle to align with $header-speed timing
    let resizeTimeout;
    const resizeHandler = () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
            // Only update if we're in mobile view where controls are visible
            if (window.innerWidth < RESPONSIVE_BREAKPOINT) {
                updateButtons();
            }
        }, 200); // 200ms to match $header-speed: .2s
    };
    
    window.addEventListener('resize', resizeHandler);
    
    // Store handler for potential cleanup
    table._swipeResizeHandler = resizeHandler;
}

function createNavButton(direction, text, clickHandler) {
    const button = document.createElement('a');
    button.className = `tablesaw-nav-btn tablesaw-btn btn-micro ${direction}`;
    button.href = '#';
    button.textContent = text;
    button.setAttribute('aria-label', `Scroll ${direction}`);
    
    // Add data attributes to reference design system
    button.setAttribute('data-transition-speed', 'header-speed');
    button.setAttribute('data-font-size', 'xs-type');
    
    button.addEventListener('click', (e) => {
        e.preventDefault();
        clickHandler();
    });
    
    return button;
}

function scrollTable(table, amount) {
    const scrollContainer = table.closest('.tablesaw-overflow') || table.parentElement;
    
    // Smooth scrolling that aligns with design system's timing
    scrollContainer.scrollTo({
        left: scrollContainer.scrollLeft + amount,
        behavior: 'smooth'
    });
}

function updateNavButtons(table, leftBtn, rightBtn) {
    const scrollContainer = table.closest('.tablesaw-overflow') || table.parentElement;
    const scrollLeft = scrollContainer.scrollLeft;
    const maxScroll = scrollContainer.scrollWidth - scrollContainer.clientWidth;
    
    // Update left button state
    if (scrollLeft <= 0) {
        leftBtn.classList.add('disabled');
        leftBtn.setAttribute('aria-disabled', 'true');
    } else {
        leftBtn.classList.remove('disabled');
        leftBtn.setAttribute('aria-disabled', 'false');
    }
    
    // Update right button state
    if (scrollLeft >= maxScroll || maxScroll <= 0) {
        rightBtn.classList.add('disabled');
        rightBtn.setAttribute('aria-disabled', 'true');
    } else {
        rightBtn.classList.remove('disabled');
        rightBtn.setAttribute('aria-disabled', 'false');
    }
}

// Utility functions that align with design system

/**
 * Get current responsive breakpoint
 * Includes custom mobile-nav breakpoint
 */
export function getCurrentBreakpoint() {
    const width = window.innerWidth;
    
    if (width >= RESPONSIVE_BREAKPOINTS.xxl) return 'xxl';
    if (width >= RESPONSIVE_BREAKPOINTS.xl) return 'xl';
    if (width >= RESPONSIVE_BREAKPOINTS['mobile-nav']) return 'mobile-nav'; // custom breakpoint
    if (width >= RESPONSIVE_BREAKPOINTS.lg) return 'lg';
    if (width >= RESPONSIVE_BREAKPOINTS.md) return 'md';
    if (width >= RESPONSIVE_BREAKPOINTS.sm) return 'sm';
    return 'xs';
}

/**
 * Check if viewport is mobile (below md breakpoint)
 */
export function isMobileView() {
    return window.innerWidth < RESPONSIVE_BREAKPOINT;
}

/**
 * Check if viewport is at custom mobile nav breakpoint
 */
export function isMobileNavBreakpoint() {
    return window.innerWidth < RESPONSIVE_BREAKPOINTS['mobile-nav'];
}

/**
 * Calculate spacing based on base-spacing system
 * @param {number} multiplier - Multiplier for $base-spacing (.5rem)
 * @returns {string} CSS value
 */
export function getCustomSpacing(multiplier) {
    return `${multiplier * 0.5}rem`; // Based on $base-spacing: .5rem
}

/**
 * Get scroll amount based on grid system
 * @param {number} columns - Number of grid columns to scroll
 * @returns {number} Pixel value
 */
export function getGridScrollAmount(columns = 4) {
    return 48 * columns; // Based on $grid-gutter-width: 48px
}